import { Controller } from '../utils/controller';

export default class extends Controller {
  static targets = ['menu', 'colorTitle', 'colorList', 'arrow', 'shadesBorder'];
  static requiredTargets = [];
  static values = {
    selected: String,
    disabledArrow: String,
    stepPointer: Number,
    listWidth: Number,
    arrowWidth: Number,
    viewWindowWidth: Number,
    listPaddingX: Number,
    selectedClassesList: [],
    fluidSwatchList: Boolean
  };

  static classes = ['selected', 'disabledArrow'];
  static requiredClasses = [];

  // TARGETS
  declare arrowTargets: HTMLButtonElement[];
  declare colorListTarget: HTMLUListElement;
  declare colorTitleTargets: HTMLDivElement[];
  declare menuTarget;
  declare readonly hasColorTitleTarget: boolean;
  declare readonly hasColorListTarget: boolean;
  declare readonly hasMenuTarget: boolean;
  declare readonly hasShadesBorderTarget: boolean;
  declare shadesBorderTarget: HTMLElement;

  // VALUES
  declare selectedValue: string;
  declare fluidSwatchListValue: boolean;

  // CLASSES
  declare selectedClasses: string[];
  declare disabledArrowClasses: string[];

  // LOCAL STATE
  private stepPointer: number;
  private listPaddingX: number;
  private arrowWidth: number;
  private viewWindowWidth: number;
  private selectedClassesList: string[];
  private listWidth: number;

  initialize() {
    this.showSelectedColorTitle();
    if (this.hasColorListTarget) {
      this.sliderInit();
    }
  }

  sliderInit() {
    const countShowedItems =
      this.hasColorListTarget && this.colorListTarget.dataset.shadesShowedItemsParam
        ? parseInt(this.colorListTarget.dataset.shadesShowedItemsParam)
        : 5;
    const shadesBorderClass =
      this.hasShadesBorderTarget && this.shadesBorderTarget.dataset.shadesBorderClassParam
        ? this.shadesBorderTarget.dataset.shadesBorderClassParam
        : '';
    const containerWidth = (this.element as HTMLElement).offsetWidth;
    const computedListStyle = getComputedStyle(this.colorListTarget);
    this.listPaddingX =
      parseFloat(computedListStyle.getPropertyValue('padding-left')) +
      parseFloat(computedListStyle.getPropertyValue('padding-right'));
    this.arrowWidth = this.arrowTargets[0]?.offsetWidth || 0;
    this.viewWindowWidth = containerWidth - this.arrowWidth * 2;
    const lis: HTMLLIElement[] = Array.from(this.element.getElementsByTagName('li'));
    this.selectedClassesList =
      this.selectedClasses.includes(shadesBorderClass) || shadesBorderClass === ''
        ? this.selectedClasses
        : [...this.selectedClasses, ...this.getClassNamesList(shadesBorderClass)];
    if (this.colorListTarget.offsetWidth < containerWidth && lis.length <= countShowedItems) {
      this.arrowTargets.forEach((item) => {
        item.hidden = true;
      });
      return;
    }

    if (!this.fluidSwatchListValue) {
      lis.forEach((li) => {
        li.style.width = `${this.viewWindowWidth / countShowedItems}px`;
        li.style.marginLeft = '0px';
        li.style.textAlign = 'center';
      });
      this.listWidth = this.colorListTarget.offsetWidth - this.listPaddingX;
    }
    this.arrowTargets[0]?.classList.add(...this.disabledArrowClasses);
    this.stepPointer = this.arrowWidth - this.listPaddingX / 2;
    this.moveListToThePointer(this.stepPointer);
  }

  getClassNamesList(classNames) {
    return classNames.split(' ').filter((element) => element);
  }

  select(event) {
    event.preventDefault();
    const skuId = event?.params?.skuId || event.target.value;
    this.selectedValue = skuId;
    this.showSelectedColorTitle();
  }

  menuTargetConnected(ele) {
    if (this.hasMenuTarget) {
      ele.value = this.selectedValue;
    }
  }

  showSelectedColorTitle() {
    if (this.hasColorTitleTarget) {
      this.colorTitleTargets.forEach((element) => {
        element.hidden = element.dataset.skuId !== this.selectedValue;
      });
    }
  }

  next(event) {
    event.preventDefault();
    this.setStepPointer('next');
    this.moveListToThePointer(this.stepPointer);
  }

  previous(event) {
    event.preventDefault();
    this.setStepPointer('previous');
    this.moveListToThePointer(this.stepPointer);
  }

  moveListToThePointer(point) {
    this.colorListTarget.style.transform = `translateX(${point}px)`;
  }

  setStepPointer(direction) {
    const delta = this.listWidth - Math.abs(this.stepPointer) - this.viewWindowWidth;

    if (direction === 'next') {
      this.arrowTargets[0].classList.remove(...this.disabledArrowClasses);

      if (delta > this.viewWindowWidth) {
        this.stepPointer = this.stepPointer - this.viewWindowWidth;
      } else if (this.stepPointer < 0) {
        this.stepPointer = this.stepPointer - (delta - this.arrowWidth + this.listPaddingX / 2);
      } else if (this.stepPointer > 0) {
        this.stepPointer = -delta;
        this.arrowTargets[1].classList.add(...this.disabledArrowClasses);
      }
    } else {
      this.arrowTargets[1].classList.remove(...this.disabledArrowClasses);

      if (Math.abs(this.stepPointer) > this.viewWindowWidth) {
        this.stepPointer = this.stepPointer + this.viewWindowWidth;
      } else {
        this.arrowTargets[0].classList.add(...this.disabledArrowClasses);
        this.stepPointer = this.arrowWidth - this.listPaddingX / 2;
      }
    }
  }

  selectedValueChanged(val, prev) {
    if (val === prev) {
      return;
    }

    // update classes on swatch elements
    const lis = Array.from(this.element.getElementsByTagName('li'));
    const prevSelectedSwatch = lis.find((li) => li.dataset.skuId == prev);
    const selectedSwatch = lis.find((li) => li.dataset.skuId == val);
    if (prevSelectedSwatch != null) {
      prevSelectedSwatch
        .getElementsByTagName('div')[0]
        .classList.remove(...this.selectedClassesList);
    }
    if (selectedSwatch != null) {
      selectedSwatch.getElementsByTagName('div')[0].classList.add(...this.selectedClassesList);
    }
    // update shades select menu
    this.updateMenuValue(val);
    // publish selection event to window
    const event = new CustomEvent('sku-selected', {
      detail: {
        skuId: val
      }
    });
    window.dispatchEvent(event);
  }

  updateMenuValue(val) {
    if (this.hasMenuTarget) {
      this.menuTarget.value = val;
    }
  }
}
